import * as React from 'react';
import { observer } from 'mobx-react';
import { Button, Row, Col } from 'antd';

import { TextView } from 'src/core/ui/text.view';
import { SupplierEditorController } from 'src/app/products/suppliers/parts/supplier-editor.controller';
import { CardController } from 'src/app/shared/card.controller';
import { DateView } from 'src/core/ui/date.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { InvoiceList } from 'src/app/accounting/shared/invoices/list/invoice-list';
import { BooleanView } from 'src/core/ui/boolean.view';
import { CurrencyEditorController } from 'src/app/general/currencies/parts/currency-editor.controller';

interface Props extends ComponentViewProps<DataSet<InvoiceSummary>> {
	onDeleteInvoice: (invoice: InvoiceSummary) => Promise<void>;
	filters?: SupplierInvoiceFilters;
	payments?: { [invoiceId: number]: SelectableInvoicePaymentPayload };
	paymentDate: Date;
	paymentMethod: number;
	paymentReference: string;
	paymentCurrencyId: number;
	onChangeFilter: (fieldName: keyof SupplierInvoiceFilters, value?: any) => void;
	onApplyFilters: () => void;
	onChangePaymentDate: (paymentDate: Date) => void;
	onChangePaymentMethod: (paymentMethod: number) => void;
	onChangePaymentReference: (paymenteReference: string) => void;
	onChangePaymentCurrencyId: (paymentCurrencyId: number) => void;
	onChangeSelection: (selectedIds: number[]) => void;
	onEditPayment: (invoiceId: number, field: keyof InvoicePaymentPayload, value: any) => void;
	onGeneratePayments: () => Promise<void>;
}

@observer
export class SupplierInvoiceFormView extends React.Component<Props, {}> {
	private handleChangeSupplier = (suppliers: SupplierSummary[]): void => {
		const { onChangeFilter, onChangePaymentCurrencyId } = this.props;
		let suppliersId: number[] = null;

		if (suppliers != null && suppliers.length > 0) {
			suppliersId = suppliers.map(m => m.id);
			onChangePaymentCurrencyId(suppliers[0].defaultCurrencyId);
		}

		onChangeFilter('suppliersId', suppliersId);
	};

	rowKey = (record: InvoiceSummary): string =>
		record.id.toString();

	render(): React.ReactNode {
		const {
			filters, onChangeFilter, onApplyFilters,
			model, onDeleteInvoice,
			paymentReference, paymentDate, paymentMethod, paymentCurrencyId,
			onChangePaymentReference, onChangePaymentDate, onChangePaymentMethod, onChangePaymentCurrencyId,
			payments, onChangeSelection, onEditPayment,
			onGeneratePayments, setBusy
		} = this.props;

		const selectedInvoices = model.items.filter(m => payments[m.id] != null && payments[m.id].checked);
		const canGenerate = paymentReference.trim() != ''
			&& selectedInvoices.length > 0
			&& selectedInvoices.every(m => m.supplierId === selectedInvoices[0].supplierId);

		return (
			<div className="window window-normal">
				<div className="panel">
					<h2>Pay Invoices</h2>
					<Row gutter={16}>
						<Col xs={6}>
							<SupplierEditorController
								multi
								mode="edit"
								label="Supplier"
								value={filters.suppliersId}
								onChange={this.handleChangeSupplier}
								setBusy={this.props.setBusy}
							/>
						</Col>
						<Col xs={6}>
							<BooleanView
								mode="edit"
								label="Show Paid Invoices"
								value={filters.showPaid}
								onChanged={(value) => onChangeFilter('showPaid', value)}
							/>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={6}>
							<TextView
								mode="edit"
								label="Booking Reference"
								value={filters.bookingReference}
								onChanged={(value) => onChangeFilter('bookingReference', value == null || value.trim() === '' ? null : value)}
							/>
						</Col>
						<Col xs={6}>
							<TextView
								mode="edit"
								label="Invoice Reference Number"
								value={filters.invoiceReferenceNumber}
								onChanged={(value) => onChangeFilter('invoiceReferenceNumber', value == null || value.trim() === '' ? null : value)}
							/>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={6}>
							<DateView
								mode="edit"
								label="Invoice Date From"
								value={filters.invoiceDateFrom}
								onChanged={(date) => onChangeFilter('invoiceDateFrom', date)}
								format="small-date"
								dateUsage="utc"
							/>
						</Col>
						<Col xs={6}>
							<DateView
								mode="edit"
								label="Invoice Date To"
								value={filters.invoiceDateTo}
								onChanged={(date) => onChangeFilter('invoiceDateTo', date)}
								format="small-date"
								dateUsage="utc"
							/>
						</Col>
						<Col xs={6}>
							<DateView
								mode="edit"
								label="Deadline"
								value={filters.dueDate}
								onChanged={(date) => onChangeFilter('dueDate', date)}
								format="small-date"
								dateUsage="utc"
							/>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={8}>
							<Button
								icon="search"
								style={{ margin: '8px auto' }}
								onClick={onApplyFilters}>
								Search
							</Button>
						</Col>
					</Row>
					<hr />
					<Row gutter={16}>
						<Col xs={10}>
						</Col>
						<Col xs={4}>
							<TextView
								required
								label="Payment Reference"
								mode="edit"
								value={paymentReference}
								onChanged={(value) => {
									onChangePaymentReference(value);
								}}
							/>
						</Col>
						<Col xs={3}>
							<DateView
								required
								label="Payment Date"
								value={paymentDate}
								onChanged={(value) => {
									onChangePaymentDate(value);
								}}
								mode="edit"
								format="small-date"
								dateUsage="utc"
							/>
						</Col>
						<Col xs={2}>
							<DropdownView
								required
								label="Method"
								model={paymentMethod}
								onChanged={(m) => {
									if (m != null) {
										onChangePaymentMethod(m.value);
									}
								}}
								options={[
									{ value: 0, label: 'Cash' },
									{ value: 1, label: 'BankTransfer' },
									{ value: 2, label: 'Card' }
								]}
							/>
						</Col>
						<Col xs={2}>
							<CurrencyEditorController
								required
								label="Payment's Currency"
								mode="edit"
								setBusy={setBusy}
								value={paymentCurrencyId}
								onChange={(value) => {
									if (value != null)
										onChangePaymentCurrencyId(value.id);
								}}
							/>
						</Col>
						<Col xs={3}>
							<Button
								icon="reload"
								className="accounting-generate-btn"
								disabled={!canGenerate}
								onClick={onGeneratePayments}>
								Generate
							</Button>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={24}>
							<CardController
								title="Invoices"
								icon="barcode"
								className="card-borderless"
								mode="display">
								<InvoiceList
									model={model}
									onDeleteInvoice={onDeleteInvoice}
									invoicePayments={payments}
									onChangeSelection={onChangeSelection}
									onEditPayment={onEditPayment}
									setBusy={setBusy}
								/>
							</CardController>
						</Col>
					</Row>
				</div>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/accounting/suppliers/invoice/supplier-invoice-form.view.tsx